import { Box, Divider, Grid, Typography } from '@mui/material';

import {
  ApprovalStatus,
  convertApprovalStatus,
  convertCompanyDocumentType,
  convertRegistrationUf,
} from '../../../../../../api/enumerations';
import { RequestNoticeCompany } from '../../../../../../api/theHive/notices/types';
import {
  formatCep,
  formatCnpjCpf,
  formatOnlyDate,
  formatPhoneNumber,
} from '../../../../../../helpers';
import { DiscreditCompanyDialog } from '../../../components/DiscreditCompanyDialog';
import { GridSection, SectionTitle, StyledInput, GridItem } from '../styles';

interface CompanyCardProps {
  noticeId: number;
  data: RequestNoticeCompany;
  callback: () => Promise<void>;
}

export function CompanyCard({
  noticeId,
  data,
  callback,
}: CompanyCardProps): JSX.Element {
  return (
    <GridSection container>
      <Grid container spacing={2}>
        <Grid item xs={6} display="flex" alignItems="center">
          <Typography fontWeight={600} color="secondary.main">
            {data.notice_name}
          </Typography>
        </Grid>
        <Grid
          display="flex"
          gap="8px"
          item
          xs={6}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Typography whiteSpace="nowrap">Pedido de credenciamento:</Typography>
          <StyledInput>
            {data.requested_at && formatOnlyDate(data.requested_at)}
          </StyledInput>
        </Grid>
        <Grid display="flex" gap="8px" item xs={6} alignItems="center">
          Status:
          <StyledInput>
            {data.approval_status &&
              convertApprovalStatus(data.approval_status)}
            <span style={{ marginLeft: '6px' }}>
              {data.approval_status === ApprovalStatus.DISCREDITED &&
                (data.discredited_reason ? 'pelo banco' : 'pela empresa')}
            </span>
          </StyledInput>
        </Grid>
        {data.approval_status === ApprovalStatus.APPROVED && (
          <Grid item xs={6} display="flex" justifyContent="flex-end">
            <DiscreditCompanyDialog
              noticeId={noticeId}
              licensedCompanyId={data.licensed_company_id}
              callback={callback}
            />
          </Grid>
        )}
        {data.approval_status === ApprovalStatus.DISCREDITED &&
          data.discredited_reason && (
            <Grid item xs={12}>
              <Box>
                Motivo do descredenciamento:
                <span style={{ fontWeight: 500, marginLeft: 8 }}>
                  {data.discredited_reason}
                </span>
              </Box>
            </Grid>
          )}
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ mt: 3 }} />
      </Grid>
      <GridItem item xs={12}>
        <SectionTitle>{data.company.name}</SectionTitle>
      </GridItem>
      <GridItem item xs={5}>
        {data.company.document_type
          ? convertCompanyDocumentType(data.company.document_type)
          : 'Documento:'}
        : <StyledInput>{formatCnpjCpf(data.company.document)}</StyledInput>
      </GridItem>
      <GridItem item xs={7}>
        {data.company.license_type?.toUpperCase() || 'Documento'} da empresa:
        <StyledInput>{data.company.license_document ?? '-'}</StyledInput>
      </GridItem>
      <GridItem item xs={5}>
        Responsável:
        <StyledInput>{data.company.responsible_user?.name}</StyledInput>
      </GridItem>
      <GridItem item xs={7}>
        {data.company.responsible_user?.professional_document_type?.toUpperCase() ||
          'Documento'}{' '}
        do responsável:
        <StyledInput>
          {data.company.responsible_user?.professional_document ?? '-'}
        </StyledInput>
      </GridItem>
      <GridItem item xs={5}>
        Telefone:
        <StyledInput>
          {formatPhoneNumber(data.company.phone_number)}
        </StyledInput>
      </GridItem>
      <GridItem item xs={7}>
        Email:
        <StyledInput>{data.company.email}</StyledInput>
      </GridItem>
      <GridItem item xs={5}>
        CEP:
        <StyledInput>
          {data.company.zipcode && formatCep(data.company.zipcode)}
        </StyledInput>
      </GridItem>
      <GridItem item xs={7}>
        Logradouro:
        <StyledInput>{data.company.address}</StyledInput>
      </GridItem>
      <GridItem item xs={5}>
        Número:
        <StyledInput>{data.company.address_number}</StyledInput>
      </GridItem>
      <GridItem item xs={7}>
        Complemento:
        <StyledInput>{data.company.address_complement}</StyledInput>
      </GridItem>
      <GridItem item xs={5}>
        Bairro:
        <StyledInput>{data.company.address_district}</StyledInput>
      </GridItem>
      <GridItem item xs={2}>
        UF:
        <StyledInput>
          {data.company.uf && convertRegistrationUf(data.company.uf)}
        </StyledInput>
      </GridItem>
      <GridItem item xs={5}>
        Cidade:
        <StyledInput>{data.company.city}</StyledInput>
      </GridItem>
    </GridSection>
  );
}
